import useUI from 'src/hooks/use-ui';

/**
 * Single source of truth for the "fullscreen-in-iframe modal" gate from
 * ECOMM-12300. Any modal opened while embedded (iframe) AND on the concierge
 * route should fill the entire iframe viewport instead of rendering as a
 * small centered card — the embedded concierge loads full inside the iframe
 * (ECOMM-12299), so a small trapped card would look broken/out of place next
 * to it, and there's no way to escape the iframe rectangle to do a native
 * full-page takeover (that's the whole premise of the ECOMM-12298 epic).
 *
 * Storefront concierge (not embedded) and tall auto-resized embedded menus
 * (embedded but not on the concierge route) both stay `false` and keep the
 * existing centered-card modal — this is intentionally narrower than "any
 * modal on any embedded menu."
 *
 * Consumed by all three `containerProps` producers -- the two
 * `useContainerProps` implementations (the legacy `modal-observer` and the
 * newer `with-modal-controls-hook`) plus the hand-rolled `containerProps`
 * object literal in `global-modals.jsx` (the pre-`withModalControlsHook`
 * modals: `Closed`, `PausedOrders`, `ResidentialVerification`,
 * `MenuTypeSwitchWarning`, etc.) -- to populate `containerProps.fullscreen`
 * for `ModalContainer`, and directly by modal content components (e.g.
 * `LoginModalContent`) that need to adapt their own internal layout
 * (header/footer placement) for the fullscreen case.
 */
export function useIsFullscreenModal(): boolean {
  const { isIframe, isConciergeActive } = useUI();

  return isIframe && isConciergeActive;
}
